import React, { useState } from "react";
import {
  Text,
  rem,
  createStyles,
  Breadcrumbs,
  useMantineTheme,
  Box,
  Avatar,
  Indicator,
  Menu,
  Group,
  Divider,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import "./HeaderBar.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { getBreadCrumbsNameFromPath } from "utils/common";
import useConfiguration from "stores/ConfigurationStore/configuration";
import useBreadcrumb, {
  BreadcrumbItem,
} from "stores/BreadcrumbStore/breadcrumb";
import { NotificationType } from "stores/ConfigurationStore/configurationType.d";

import { NOTIFICATIONS_TYPE } from "constants/enum";
import RoutesString from "pages/routesString";
import { useClickOutside } from "@mantine/hooks";
// import LanguagePicker from "components/utils/language-picker/LanguagePicker";
import NotificationsMenu from "components/notification/NotificationsMenu";
import SearchBar from "./component/SearchBar";
import useAuth from "@/hooks/useAuth";
import UserDropdown from "../logged-in-layout/UserDropdown";
import JavisAiChat from "@/components/common/javis-ai-chat/JavisAiChat";
import i18n from "i18n/i18n";

type HeaderBarProps = {
  userInfo: any;
  onMenuClick(): void;
  onUserBlockClick(): void;
};

const useStylesUserBlock = createStyles((theme) => ({
  user: {
    display: "block",
    width: rem(250),
    padding: theme.spacing.md,
    color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.black,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[8]
          : theme.colors.gray[0],
    },
  },
}));

const useStylesBagde = createStyles((theme) => ({
  badge: {
    position: "absolute",
    top: 0,
    width: rem(38),
    right: rem(-33),
  },
  badgeBig: {
    position: "absolute",
    top: 0,
    right: rem(-39),
  },
  badgeWrapper: {
    position: "relative",
    marginTop: rem(12),
    height: rem(50),
    paddingTop: rem(8),
    marginRight: rem(20),
    marginLeft: rem(10),
    cursor: "pointer",
  },
}));

const useStylesAvatar = createStyles((theme) => ({
  placeholder: {
    backgroundColor: theme.colors.primary[7],
    color: theme.colors.white,
  },
}));

const useStylesTabsBlock = createStyles((theme) => ({
  tab: {
    color: theme.colors.dark,
    fontSize: rem(16),
    fontWeight: 600,
    height: rem(50),
    paddingLeft: rem(30),
    paddingRight: rem(30),
    "&[data-active]": {
      color: theme.colors.primary[7],
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  item: {
    display: "block",
    width: "100%",
    padding: theme.spacing.md,
    color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.black,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[8]
          : theme.colors.gray[0],
    },
  },

  read: {
    position: "absolute",
    bottom: 0,
    width: "98%",
    height: rem(50),
    zIndex: 9999999,
    borderTop: `${rem(1)} solid ${theme.colors.gray[3]}`,
    backgroundColor: theme.colors.white[0],
    paddingLeft: rem(20),
    paddingRight: rem(20),
  },

  textRead: {
    cursor: "pointer",
    "&:hover": {
      color: theme.colors.primary[7],
    },
  },
}));

// Một số mục cha không có trang riêng → trỏ về trang danh sách tương ứng
// để bấm breadcrumb không bị 404.
const PARENT_PATH_REDIRECT: Record<string, string> = {
  [RoutesString.Cards]: RoutesString.CardsList, // "/cards" -> "/cards/list-cards"
};

// Inject thêm breadcrumb cha khi URL bỏ qua một level trung gian
const INJECT_PARENT_BREADCRUMB: Record<string, { title: string; href: string }> = {
  "detail-current-account": {
    title: getBreadCrumbsNameFromPath("list-account"),
    href: RoutesString.AccountsList,
  },
};

// Tập hợp các route hợp lệ (không chứa tham số ":") để xác định mục breadcrumb
// nào thực sự điều hướng được — tránh 404 khi bấm vào mục cha không có trang.
const VALID_ROUTE_PATHS = new Set(
  Object.values(RoutesString).filter(
    (value): value is string =>
      typeof value === "string" && !value.includes(":"),
  ),
);

// Một số path tồn tại trong RoutesString nhưng KHÔNG có <Route> tương ứng
// (mục cha gom nhóm menu, không có trang riêng). Loại khỏi danh sách điều hướng
// để bấm breadcrumb không bị 404. VD: "/term-deposit" chỉ là nhóm "Tiền gửi",
// trang thật là "/term-deposit/term-deposit-list".
const NON_NAVIGABLE_PATHS = new Set<string>([RoutesString.TermDeposit]);

// #55667: bản thiết kế của module Bảo lãnh vẽ ô tìm kiếm trên header BUNG SẴN
// ("Nhập thông tin tìm kiếm của bạn..."), trong khi toàn app chỉ hiện icon kính lúp.
// Thu gọn là hành vi CÓ CHỦ Ý của header chung, nên chỉ bật bung sẵn cho riêng
// module này thay vì đổi mặc định của `SearchBar` (mặc định vẫn là thu gọn).
// So khớp theo BIÊN SEGMENT, không dùng `startsWith("/guarantee")` trần: các route
// `/guarantee-issuance` và `/credit/guarantee` là màn cũ KHÁC module, sẽ bị nuốt oan.
const isGuaranteeModulePath = (pathname: string) => {
  const base = RoutesString.GuaranteeListAw122Page; // "/guarantee"
  return pathname === base || pathname.startsWith(`${base}/`);
};

const getBreadCrumbsFromLocation = (pathname: string) => {
  // #55667: màn KẾT QUẢ GIAO DỊCH (`s-done` của bản mẫu) nằm dưới /guarantee/issue/* nhưng KHÔNG
  // thuộc wizard — BCMAP của bản mẫu ghi `"s-done": "Kết quả giao dịch"`. Phải chặn TRƯỚC nhánh
  // wizard ngay bên dưới, nếu không `startsWith("/guarantee/issue")` nuốt mất và breadcrumb ra
  // nhầm "Bảo lãnh › Yêu cầu phát hành bảo lãnh" (tester nêu 22/09). Chỉ thêm đúng route này,
  // không đụng route nào khác.
  if (pathname.startsWith(RoutesString.GuaranteeIssueResultPage)) {
    return [
      { title: i18n.t("GUARANTEE"), href: RoutesString.GuaranteeListAw122Page },
      { title: i18n.t("TRANSACTION_RESULT"), href: null },
    ];
  }

  // Luồng phát hành bảo lãnh (guarantee/issue/*): breadcrumb cố định "Bảo lãnh › Yêu cầu phát hành
  // bảo lãnh", KHÔNG có "Tổng quan" và KHÔNG tách theo từng segment — cả 4 bước wizard là một trang.
  // Tính đồng bộ ngay trong render nên không nhấp nháy khi chuyển bước (khác kiểu set qua store,
  // vốn về null lúc unmount làm breadcrumb hiển thị sai trong lúc chuyển màn).
  if (pathname.startsWith(`${RoutesString.GuaranteeListAw122Page}/issue`)) {
    return [
      { title: i18n.t("GUARANTEE"), href: RoutesString.GuaranteeListAw122Page },
      { title: i18n.t("GUARANTEE_ISSUANCE_REQUEST"), href: null },
    ];
  }

  // #55599: các màn còn lại của module Bảo lãnh. Không segment nào (`guarantee`, `request`,
  // `supplement`, `letter`) có case trong `getBreadCrumbsNameFromPath` nên tất cả trả chuỗi rỗng
  // rồi bị lọc, breadcrumb rơi về mỗi "Tổng quan" — đúng chỗ tester khoanh (22/09: "Lỗi đường dẫn
  // chức năng"). Bản thiết kế (Bao_Lanh_Du_Thau_v2.html, BCMAP) quy định HAI cấp cho mọi màn:
  // gốc "Bảo lãnh" (bấm được, về danh sách) › tên màn hiện tại.
  //
  // Cấp hai luôn `href: null` — nó là màn đang đứng, đúng khuôn hai nhánh `issue/*` ở trên.
  //
  // So khớp BẰNG (===) chứ không startsWith: các route này đều không có tham số trên path
  // (mọi màn chi tiết nhận id qua query string, xem `routes.tsx`), nên `===` là đủ và tránh hẳn
  // bẫy thứ tự — `/guarantee/request` mà dùng startsWith sẽ nuốt `/guarantee/request/supplement`.
  // Dù vậy vẫn đặt `supplement` TRƯỚC `request` để nếu sau này ai đổi sang startsWith thì thứ tự
  // đã đúng sẵn.
  //
  // KHÔNG liệt kê `/guarantee/approval` và `/guarantee/approval/detail`: hai màn đó tự khai
  // breadcrumb qua store `useBreadcrumb` (#55665), và `breadcrumbOverride` được ưu tiên hơn hàm này.
  if (pathname === RoutesString.GuaranteeSupplementPage) {
    return [
      { title: i18n.t("GUARANTEE"), href: RoutesString.GuaranteeListAw122Page },
      { title: i18n.t("SUPPLEMENT_FILE"), href: null },
    ];
  }

  if (pathname === RoutesString.GuaranteeRequestDetailPage) {
    return [
      { title: i18n.t("GUARANTEE"), href: RoutesString.GuaranteeListAw122Page },
      { title: i18n.t("REQUEST_DETAILS"), href: null },
    ];
  }

  if (pathname === RoutesString.GuaranteeLetterDetailPage) {
    return [
      { title: i18n.t("GUARANTEE"), href: RoutesString.GuaranteeListAw122Page },
      { title: i18n.t("GUARANTEE_DETAILS"), href: null },
    ];
  }

  if (pathname === RoutesString.GuaranteeListAw122Page) {
    return [
      { title: i18n.t("GUARANTEE"), href: RoutesString.GuaranteeListAw122Page },
      { title: i18n.t("GUARANTEE_LIST"), href: null },
    ];
  }

  const arrPath = pathname.split("/").slice(1);
  let path = "";
  const breadCrumbs: ({ title: string; href: string | null } | null)[] = [];

  arrPath.forEach((item, index) => {
    path += `/${item}`;
    if (!item) return;

    // Inject breadcrumb cha nếu cần (VD: detail-current-account cần có list-account trước)
    const injection = INJECT_PARENT_BREADCRUMB[item];
    if (injection) {
      breadCrumbs.push({ title: injection.title, href: injection.href });
    }

    // JVC-3515: URL /cards/transaction-detail/:cardId/:transId nhảy cấp,
    // thiếu mục "Lịch sử giao dịch" → chèn lại cho khớp thiết kế.
    if (item === "transaction-detail") {
      const cardIdSeg = arrPath[index + 1];
      breadCrumbs.push({
        title: i18n.t("VIEW_TRANSACTION_HISTORY"),
        href: cardIdSeg
          ? `${RoutesString.CardTransactionHistory}/${cardIdSeg}`
          : RoutesString.CardTransactionHistory,
      });
    }

    const isLast = arrPath.length === index + 1;
    const resolvedHref = NON_NAVIGABLE_PATHS.has(path)
      ? null
      : PARENT_PATH_REDIRECT[path] ?? (VALID_ROUTE_PATHS.has(path) ? path : null);
    breadCrumbs.push({
      title: getBreadCrumbsNameFromPath(item),
      href: isLast ? null : resolvedHref,
    });
  });

  return [
    { title: i18n.t("SUMMARY"), href: RoutesString.Dashboard },
    ...breadCrumbs,
  ];
};

const NOTIFICATION_MAXIMUM = "99+";

const HeaderBar: React.FC<HeaderBarProps> = ({
  userInfo,
  onMenuClick,
  onUserBlockClick,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const [statesConfiguration] = useConfiguration();
  const [{ items: breadcrumbOverride }] = useBreadcrumb();
  const [openedMenu, setOpenedMenu] = useState(false);
  const { classes: classesUserBlock } = useStylesUserBlock();
  const { classes: classesAvatar } = useStylesAvatar();
  const { classes: classesBadge } = useStylesBagde();
  const { classes: classesTabsBlock } = useStylesTabsBlock();
  const { user } = useAuth();

  const ref = useClickOutside(() => setOpenedMenu(false));

  const breadcrumbSource: (BreadcrumbItem | null)[] =
    breadcrumbOverride ?? getBreadCrumbsFromLocation(location.pathname);
  const breadcrumbItems = breadcrumbSource.filter((item) => item?.title);
  const breadcrumbs = breadcrumbItems.map((item, index) => {
    // Mục cuối cùng = trang hiện tại → luôn là text, không cho bấm (tránh 404
    // khi segment cuối là tham số bị ẩn, vd /cards/detail-card/:cardId).
    const isLastVisible = index === breadcrumbItems.length - 1;
    if (isLastVisible) {
      return (
        <Text
          fz="sm"
          color={item?.color ?? "#0E121B"}
          fw={600}
          key={`${index + 1}`}
        >
          {item?.title}
        </Text>
      );
    }
    return item?.href ? (
      <Text
        key={`${index + 1}`}
        fz="sm"
        color={theme.colors.dark[3]}
        style={{ cursor: "pointer" }}
        onClick={() => navigate(item.href as string)}
      >
        {decodeURIComponent(item.title)}
      </Text>
    ) : (
      <Text key={`${index + 1}`} fz="sm" color={theme.colors.dark[3]}>
        {decodeURIComponent(item?.title ?? "")}
      </Text>
    );
  });

  const getBagdeFromNotificationLength = (
    notifications: NotificationType[],
  ) => {
    const notificationUnread = notifications.filter(
      (item) => item.viewStatus === "Y",
    );
    if (notificationUnread.length < 100 && notificationUnread.length > 0) {
      return notifications.length.toString();
    }

    if (notificationUnread.length >= 100) {
      return NOTIFICATION_MAXIMUM;
    }

    return "";
  };

  const notificationInfo = statesConfiguration.notifications
    .filter(
      (item) =>
        item.type !== NOTIFICATIONS_TYPE.PROMOTION && item.viewStatus === "Y",
    )
    .slice(0, 50);

  const notificationTransaction = statesConfiguration.notifications
    .filter(
      (item) =>
        (item.productCategoryCode === "CT" ||
          item.productCategoryCode === "TRANSFER") &&
        item.viewStatus === "Y",
    )
    .slice(0, 50);

  const notificationPromocy = statesConfiguration.notifications
    .filter(
      (item) =>
        item.type === NOTIFICATIONS_TYPE.PROMOTION && item.viewStatus === "Y",
    )
    .slice(0, 50);

  const markAsRead = () => {};

  const onItemClick = (item: NotificationType) => {
    // const transId = get(item, "transId", "");
    // navigate(`${RoutesString.TransactionDetail}/${transId}`, {
    //   state: { type: "TRANS" },
    // });
    // setOpenedMenu(false);
  };

  return (
    <Box
      sx={{
        overflow: "visible",
        position: "fixed",
        top: 0,
        width: "calc(100% - var(--mantine-navbar-width, 0px))",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        zIndex: 99,
        backgroundColor: "#F8F9FB",
        padding: "16px 20px 0px 20px",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          height: "64px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 19px",
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          backgroundColor:
            location.pathname === RoutesString.Dashboard
              ? "transparent"
              : "#FDFDFD",
        }}
      >
        {location.pathname === RoutesString.Dashboard ? (
          <Box
            style={{
              height: "30px",
              width: "120px",
            }}
          >
            {/* <Image src={logoImage} style={{ height: "30px", width: "auto" }} /> */}
          </Box>
        ) : (
          <Box
            sx={{
              height: "2.55rem",
              width: "100%",
              paddingRight: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Breadcrumbs
              separator={
                <Text fz="sm" color={theme.colors.dark[3]}>/</Text>
              }
            >
              {breadcrumbs}
            </Breadcrumbs>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {/* <LanguagePicker /> */}
          <SearchBar alwaysExpanded={isGuaranteeModulePath(location.pathname)} />
          {/* Trợ lý ảo Javis AI — design 14/08/2026 đặt ở màn Tổng quan. Bỏ điều kiện dưới đây
              là nút theo người dùng đi khắp mọi màn. */}
          {location.pathname === RoutesString.Dashboard && <JavisAiChat />}
          <Divider orientation="vertical" h={12} />
          <NotificationsMenu />

          <Menu shadow="md" width={270} position="bottom-end">
            <Menu.Target>
              <Group
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Indicator
                  inline
                  size={12}
                  offset={10}
                  position="bottom-end"
                  color="red"
                  withBorder
                >
                  <Avatar src="" alt="" size={40} radius="xl" />
                </Indicator>
              </Group>
            </Menu.Target>

            {user && <UserDropdown userInfo={user} />}
          </Menu>
        </Box>
      </Box>
      {/* Breadcrumbs */}
    </Box>
  );
};

export default HeaderBar;
